import { Badge } from 'primereact/badge';
import { TreeNode } from 'primereact/treenode';
import { TreeSelect, TreeSelectChangeEvent, TreeSelectExpandedEvent } from 'primereact/treeselect';
import { classNames } from 'primereact/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import countries from '../../../assets/static/countries.json';
import { GetComponentsParams } from '../../../hooks/api/components';
import {
  CountryCodeToLabelMap,
  extractFullyCheckedNodes,
  findNodeByKey,
  RegionCodeToLabelMap,
} from '../../helpers/country-region-helpers';
import { prepareFlagIconsRecursively } from '../../helpers/prepare-flag-icons-recursively';
import { parseMulti } from '../../helpers/url-parameter-parser';
import styles from './LocationFilter.module.css';
interface LocationsFilterProps {
  onLocationsFilterChange: (countries: string[], regions: string[]) => void;
  filters?: Partial<GetComponentsParams>;
}
const LocationFilter = ({
  filters,
  onLocationsFilterChange,
}: LocationsFilterProps): JSX.Element => {
  const { t } = useTranslation();
  const [selectedKeys, setSelectedKeys] = useState<{
    [key: string]: { checked: boolean; partiallyChecked: boolean };
  }>({});

  const [expandedKeys, setExpandedKeys] = useState({});
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  useEffect(() => {
    prepareFlagIconsRecursively(countries);
  }, [countries]);

  // Function to prepare initial selected nodes based on the query string params
  // e.g. if you directly visit https://..../products?country=DE,FR&region=global,sub_saharan_africa
  // then you should see the selected nodes as Germany, France, Global, Sub-Saharan Africa and all sub-countries under those regions
  const prepareInitialNodeKeys = (): void => {
    // Get the array of country keys and region keys from parent, i.e. the URL
    const countryKeys = parseMulti(filters?.country); // e.g. ['DE', 'FR']
    const regionKeys = parseMulti(filters?.region); // e.g. ['global', 'sub_saharan_africa']

    // Map to store the selected nodes to be set in the state, to show them selected in the tree
    const preSelectedNodeKeys: { [key: string]: { checked: boolean; partiallyChecked: boolean } } =
      {};

    // Fill the map with countries
    countryKeys.forEach((countryKey) => {
      preSelectedNodeKeys[countryKey] = { checked: true, partiallyChecked: false };
    });

    // Fill the map with regions
    // Note: If a region is seleceted, that means all the countries/sub-regions in that region must be selected as well
    regionKeys.forEach((regionKey) => {
      const regionNode = findNodeByKey(countries, regionKey);

      // Select the region itself first
      preSelectedNodeKeys[regionKey] = { checked: true, partiallyChecked: false };

      // Then select all the countries/sub-regions in that region
      const childrenNodes = regionNode?.children || [];

      childrenNodes.forEach((childNode) => {
        if (!childNode.children) {
          // If this child is a country, select it
          preSelectedNodeKeys[childNode.key as string] = { checked: true, partiallyChecked: false };
        } else {
          // Otherwise, it's a sub-region, so select all the countries in that sub-region
          childNode.children.forEach((subRegionChildNode) => {
            preSelectedNodeKeys[subRegionChildNode.key as string] = {
              checked: true,
              partiallyChecked: false,
            };
          });

          // Also don't forget to select the sub-region itself
          preSelectedNodeKeys[childNode.key as string] = { checked: true, partiallyChecked: false };
        }
      });
    });

    // Now higlight those nodes in the tree
    setSelectedKeys(preSelectedNodeKeys);
  };

  // Function to handle when you make a change in the selections in the tree
  const onChange = (e: TreeSelectChangeEvent) => {
    const selectedKeysMap =
      (e.value as { [key: string]: { checked: boolean; partiallyChecked: boolean } }) || {}; // e.g. { DE: true, FR: true, global: true, europe: true }
    const fullyCheckedNodeKeysMap = extractFullyCheckedNodes(selectedKeysMap);

    setSelectedKeys(selectedKeysMap);

    const countryFilter: string[] = []; // e.g. ['DE', 'FR']
    const regionFilter: string[] = []; // e.g. ['global', 'sub_saharan_africa']

    // Differentiate countries and regions based on the keys
    // This step is necessary because we can't tell if a key belongs to a country or region just by looking at it
    Object.keys(fullyCheckedNodeKeysMap).forEach((key) => {
      if (CountryCodeToLabelMap[key]) {
        // If it's a country
        countryFilter.push(key);
      }

      if (RegionCodeToLabelMap[key]) {
        // If it's a region
        regionFilter.push(key);
      }
    });

    // Update the filter values in the parent component, which will eventually update the URL
    onLocationsFilterChange(countryFilter, regionFilter);
  };

  // Function to handle when you expand a node in the tree
  // This is necessary to keep track of the expanded nodes
  // Also, because of a bug in the TreeSelect component, if you don't handle this, some other random nodes can be toggled when you expand an irrelevant node
  const onToggle = (e: TreeSelectExpandedEvent) => {
    setExpandedKeys(e.value);
  };

  // Based on the filter values that initially come from the parent component (from URL actually), set the selected nodes
  useEffect(() => {
    prepareInitialNodeKeys();
  }, [filters?.country, filters?.region]);

  const valueTemplate = (nodes: TreeNode[] | TreeNode | null | undefined) => {
    // If nothing is selected, show the placeholder
    if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
      return <span>{t('componentsPage.locations')}</span>;
    }

    // Display the number of selected nodes
    return (
      <div className='flex flex-row gap-1 line-height-2'>
        <span className='text-primary'>{t('componentsPage.locations')}:</span>
        <Badge className='flex align-items-center justify-content-center' value={nodes.length} />
      </div>
    );
  };

  return (
    <TreeSelect
      value={selectedKeys}
      options={countries}
      selectionMode='checkbox'
      valueTemplate={valueTemplate}
      onChange={onChange}
      expandedKeys={expandedKeys}
      filterPlaceholder={t('componentsPage.filterPlaceholder')}
      onToggle={onToggle}
      onHide={() => {
        setIsOverlayVisible(false);
      }}
      onShow={() => {
        setIsOverlayVisible(true);
      }}
      showClear
      filter
      filterInputAutoFocus
      className={classNames(
        styles['location-filter'],
        isOverlayVisible ? styles['location-filter-open'] : styles['location-filter-close'],
      )}
      pt={{
        root: {
          className: styles['root'],
        },
        wrapper: {
          className: styles['overlay-wrapper'],
        },
        labelContainer: {
          className: 'max-h-full',
        },
        label: {
          className: styles['label'],
        },
        closeButton: {
          className: 'hidden',
        },
        clearIcon: {
          className: classNames(
            'text-black-alpha-60',
            {
              hidden: selectedKeys === undefined || Object.keys(selectedKeys).length === 0,
            },
            styles['clear-icon'],
          ),
        },
        trigger: {
          className: classNames({
            'hidden w-0 p-0 overflow-hidden shrink-0':
              selectedKeys && Object.keys(selectedKeys).length > 0,
          }),
        },
      }}
    />
  );
};

export default LocationFilter;
